import { Alert, Card, Col, Row, Skeleton, Statistic, Table, Tag, Typography } from 'antd';
import { errorMessage } from '../../api/client';
import { useFixedAccount } from '../../api/hooks';
import type { FixedInvestment, PartyType } from '../../types';
import { date, money } from '../../utils/format';
import { useTranslation } from 'react-i18next';

interface Props {
  partyType: PartyType;
  partyId: number;
}

/**
 * Profit on the fixed-return book. There is no daily accrual here — the profit is fixed at the
 * moment the deal is struck, as the difference between the capital handed over and the sum agreed
 * to come back — so this states the agreed profit per investment rather than an accrual timeline.
 *
 * Read-only by design: money leaves a fixed account through the Withdrawals tab, drawn against the
 * agreed return as a whole. There is no separate profit payment to record.
 */
export default function FixedProfitTab({ partyType, partyId }: Props) {
  const { t } = useTranslation();
  const { data: account, isLoading, error } = useFixedAccount(partyType, partyId);

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !account) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Total profit agreed" value={money(account.totalProfit)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title={t('finance.totalReturnAgreed')}
              value={money(account.totalReturnAgreed)}
              prefix="PKR"
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title={t('finance.remainingAmount')}
              value={money(account.totalRemaining)}
              prefix="PKR"
              valueStyle={{ color: account.totalRemaining > 0 ? '#FF0000' : '#3EB780' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Profit by investment" style={{ marginTop: 16 }} size="small">
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Profit = agreed return − amount invested. Fixed at the time of the deal, so it does not
          accrue day by day.
        </Typography.Paragraph>
        <Table<FixedInvestment>
          rowKey="id"
          size="small"
          dataSource={account.investments}
          pagination={false}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: '#', dataIndex: 'id', width: 70 },
            { title: t('finance.investmentDate'), dataIndex: 'investDate', render: date },
            { title: t('forms.returnDueOn'), dataIndex: 'dueDate', render: date },
            { title: t('finance.totalAmount'), dataIndex: 'amount', align: 'right', render: money },
            { title: t('finance.totalReturnAgreed'), dataIndex: 'returnAmount', align: 'right', render: money },
            {
              title: 'Profit Amount',
              dataIndex: 'profitAmount',
              align: 'right',
              render: (v: number) => <strong>{money(v)}</strong>,
            },
            { title: t('finance.totalWithdrawn'), dataIndex: 'withdrawnAmount', align: 'right', render: money },
            { title: t('finance.totalRemaining'), dataIndex: 'remainingAmount', align: 'right', render: money },
            {
              title: t('common.status'),
              dataIndex: 'isActive',
              render: (active: boolean) => (
                <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Closed'}</Tag>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
